import React from 'react';

// =============================================
// BUDGET COMPONENTS  —  NG-1 Enhanced
// =============================================

// ── NG-1: Inline spend-entry micro-form ──────────────────────
export function SpendEntryForm({ onSubmit, symbol }) {
    const [amt, setAmt] = React.useState('');
    const [note, setNote] = React.useState('');

    const handleSubmit = () => {
        const n = parseFloat(amt);
        if (!n || n <= 0) return;
        onSubmit(n, note.trim());
        setAmt('');
        setNote('');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSubmit();
    };

    return (
        <div className="spend-entry-form">
            <input
                type="number"
                className="spend-amt-input"
                value={amt}
                onChange={e => setAmt(e.target.value === '' ? '' : e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`${symbol} Amount`}
                inputMode="decimal"
            />
            <input
                type="text"
                className="spend-note-input"
                value={note}
                onChange={e => setNote(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Note (optional)"
                maxLength={60}
            />
            <button className="spend-submit-btn" onClick={handleSubmit}>+ Log</button>
        </div>
    );
}

// ── NG-1: Enhanced BudgetRow with utilisation bar + spend log ─
export function BudgetRow({ item, index, accent, currency, onToggle, onCategoryChange, onAmountChange, onDescChange, onDelete, hasDesc, onAddSpend, isLogOpen, onToggleLog, symbol }) {
    const budget = parseFloat(item.amount) || 0;
    const spent = item.actualSpent || 0;
    const pct = budget > 0 ? Math.min((spent / budget) * 100, 100) : 0;
    const barColor = pct >= 90 ? 'var(--danger)' : pct >= 70 ? '#F59E0B' : 'var(--secondary)';
    const showUtil = item.enabled && budget > 0;

    return (
        <div className={`budget-row ${!item.enabled ? 'disabled' : ''}`}>
            {/* — existing row top — */}
            <div className="budget-row-top">
                <input
                    type="checkbox"
                    className="budget-toggle"
                    checked={item.enabled}
                    onChange={onToggle}
                />
                <input
                    type="text"
                    className="budget-category-input"
                    value={item.category}
                    onChange={onCategoryChange}
                    placeholder="Category"
                />
                <input
                    type="number"
                    className="budget-amount-input"
                    value={item.amount ?? ''}
                    onChange={onAmountChange}
                    placeholder="0"
                    style={{ color: accent }}
                />
                <button className="budget-delete-btn" onClick={onDelete} title="Remove">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" />
                    </svg>
                </button>
            </div>

            {/* — optional description — */}
            {hasDesc && (
                <input
                    type="text"
                    className="budget-desc-input"
                    value={item.description || ''}
                    onChange={onDescChange}
                    placeholder="Notes / items..."
                />
            )}

            {/* ── NG-1: utilisation bar + log toggle ── */}
            {showUtil && (
                <div className="budget-utilisation-wrap">
                    <div className="budget-util-bar-track">
                        <div
                            className="budget-util-bar-fill"
                            style={{ width: `${pct}%`, background: barColor }}
                        />
                    </div>
                    <div className="budget-util-meta">
                        <span className="budget-util-spent-text">
                            {symbol}{spent.toLocaleString()} spent
                        </span>
                        <button
                            className="spend-log-toggle-btn"
                            style={{ color: isLogOpen ? 'var(--text-tertiary)' : accent }}
                            onClick={onToggleLog}
                        >
                            {isLogOpen ? '▲ Close' : '＋ Log Spend'}
                        </button>
                        <span className="budget-util-pct-text" style={{ color: barColor }}>
                            {Math.round(pct)}%
                        </span>
                    </div>
                </div>
            )}

            {/* ── NG-1: inline spend panel ── */}
            {isLogOpen && item.enabled && (
                <div className="spend-log-panel">
                    <SpendEntryForm onSubmit={onAddSpend} symbol={symbol} />
                    <div className="spend-history-list">
                        {(item.spendLog && item.spendLog.length > 0) ? (
                            [...item.spendLog].reverse().map((entry, i) => (
                                <div key={i} className="spend-history-row">
                                    <span className="spend-hist-date">{entry.date}</span>
                                    <span className="spend-hist-note">{entry.note || '—'}</span>
                                    <span className="spend-hist-amount" style={{ color: accent }}>
                                        {symbol}{parseFloat(entry.amount).toLocaleString()}
                                    </span>
                                </div>
                            ))
                        ) : (
                            <div className="spend-history-empty">No spend entries yet for this item.</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ── NG-1: CycleUtilisationCard — shown above each BudgetGroup ─
export function CycleUtilisationCard({ items, symbol, accent }) {
    const enabledItems = items.filter(i => i.enabled && (i.amount || 0) > 0);
    if (enabledItems.length === 0) return null;

    const totalBudget = enabledItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    const totalSpent = enabledItems.reduce((s, i) => s + (i.actualSpent || 0), 0);
    const pct = totalBudget > 0 ? Math.min((totalSpent / totalBudget) * 100, 100) : 0;
    const barColor = pct >= 90 ? 'var(--danger)' : pct >= 70 ? '#F59E0B' : 'var(--secondary)';
    const remaining = totalBudget - totalSpent;

    return (
        <div className="cycle-utilisation-card">
            <div className="cycle-util-row">
                <span className="cycle-util-label">Cycle Budget</span>
                <div className="cycle-util-values">
                    <span>Budget: <strong style={{ color: 'var(--text-primary)' }}>{symbol}{totalBudget.toLocaleString()}</strong></span>
                    <span>Spent: <strong className="cycle-util-spent" style={{ color: barColor }}>{symbol}{totalSpent.toLocaleString()}</strong></span>
                    <span style={{ color: remaining >= 0 ? 'var(--secondary)' : 'var(--danger)' }}>
                        {remaining >= 0 ? '↓' : '↑'} {symbol}{Math.abs(remaining).toLocaleString()} {remaining >= 0 ? 'left' : 'over'}
                    </span>
                </div>
            </div>
            <div style={{ marginTop: '4px' }}>
                <div className="budget-util-bar-track" style={{ height: '6px' }}>
                    <div className="budget-util-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <span className="cycle-util-pct" style={{ color: barColor }}>{Math.round(pct)}% utilised</span>
            </div>
        </div>
    );
}

// ── NG-1: Enhanced BudgetGroup with open-log state + add-spend handler ─
export function BudgetGroup({ title, items, accent, currency, symbol, hasDesc, onAdd, onUpdate, onDelete, onAddSpend }) {
    const [openLogId, setOpenLogId] = React.useState(null);

    const total = items.filter(i => i.enabled).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    const totalSpent = items.filter(i => i.enabled).reduce((s, i) => s + (i.actualSpent || 0), 0);

    const handleToggleLog = (itemId) => {
        setOpenLogId(prev => prev === itemId ? null : itemId);
    };

    return (
        <div className="budget-group">
            <div className="budget-group-header">
                <span className="budget-group-label">{title}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ textAlign: 'right' }}>
                        <span className="budget-group-total" style={{ color: accent }}>
                            {symbol}{total.toLocaleString()}
                        </span>
                        {totalSpent > 0 && (
                            <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '1px' }}>
                                {symbol}{totalSpent.toLocaleString()} spent
                            </div>
                        )}
                    </div>
                    <button className="budget-add-btn" onClick={onAdd}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        Add
                    </button>
                </div>
            </div>
            {items.length === 0 && (
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '13px' }}>
                    No items yet — tap Add to get started
                </div>
            )}
            {items.map((item, index) => (
                <BudgetRow
                    key={item.id}
                    item={item}
                    index={index}
                    accent={accent}
                    currency={currency}
                    symbol={symbol}
                    hasDesc={hasDesc}
                    isLogOpen={openLogId === item.id}
                    onToggle={() => onUpdate(index, { ...item, enabled: !item.enabled })}
                    onCategoryChange={e => onUpdate(index, { ...item, category: e.target.value })}
                    onAmountChange={e => onUpdate(index, { ...item, amount: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0 })}
                    onDescChange={e => onUpdate(index, { ...item, description: e.target.value })}
                    onDelete={() => onDelete(index)}
                    onToggleLog={() => handleToggleLog(item.id)}
                    onAddSpend={(amount, note) => {
                        onAddSpend(item.id, amount, note);
                    }}
                />
            ))}
        </div>
    );
}